import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SwiperSlide, Swiper } from "swiper/react";
import SwiperCore, { Pagination } from "swiper";
import ReactWhatsapp from "react-whatsapp";
import { AboutHJSection } from "../../../sections/aboutHJ";
import { WhyHJSection } from "../../../sections/whyHJSection";
import config from "../../../configuration/config";
import Offer from "../../../components/offer";
import { slugCombine } from "../../../utils/slugManager";
import { getData, saveData } from "../../../utils/localStorageManager";
import InlineBookingCard from "../components/InlineBookingCard";
import FloatingMaintenanceBg from "../components/FloatingMaintenanceBg";
import ProtectionAndShadingHomeSection from "../../home/components/ProtectionAndShadingHomeSection";
import "swiper/swiper.scss";
import "swiper/components/pagination/pagination.scss";

SwiperCore.use([Pagination]);

const getServiceIcon = (title) => {
    if (!title) return `url("/assets/icons/services/general.svg")`;
    const lower = title.toLowerCase();
    if (lower.includes("body") || lower.includes("paint")) return `url("/assets/icons/services/painting.svg")`;
    if (lower.includes("periodic")) return `url("/assets/icons/services/maintenance-periodic.svg")`;
    if (lower.includes("mechanic")) return `url("/assets/icons/services/mechanics.svg")`;
    if (lower.includes("diagnosis")) return `url("/assets/icons/services/diagonise.svg")`;
    return `url("/assets/icons/services/general.svg")`;
};

const MainServicePage = () => {
    const { t: trans, i18n } = useTranslation(["info", "navbar"]);
    const lang = i18n.language || "ar";

    const [mainService, setMainService] = useState([]);
    const [maintenanceOffers, setMaintenanceOffers] = useState([]);
    const [testimonials, setTestimonials] = useState([]);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 769);
    const [preselectedServiceId, setPreselectedServiceId] = useState(null);

    useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth < 769);
        window.addEventListener("resize", onResize);
        const t = setTimeout(() => window.dispatchEvent(new Event("resize")), 500);
        return () => { window.removeEventListener("resize", onResize); clearTimeout(t); };
    }, []);


    useEffect(() => {
        async function fetchMainService() {
            try {
                if ("caches" in window) {
                    const cache = await caches.open("MainServicePage");
                    const cached = await cache.match("site_fetchMainService");
                    const data = await cached?.json();
                    if (data?.length) setMainService(data);
                }
            } catch { /* ignore */ }
            try {
                const res = await axios.post(
                    `${config.controllURL}${lang}/api/Pages/site_fetchMainService`,
                    {}
                );
                setMainService(res.data || []);
                if ("caches" in window) {
                    const cache = await caches.open("MainServicePage");
                    cache.put("site_fetchMainService", new Response(JSON.stringify(res.data)));
                }
            } catch { /* ignore */ }
        }
        async function fetchOffers() {
            try {
                const res = await axios.get(`${config.controllURL}${lang}/api/Maintenance/MaintenanceOffers`);
                setMaintenanceOffers(res.data || []);
            } catch { /* ignore */ }
        }
        async function fetchTestimonials() {
            // Mirror src/sections/testimonialSection.jsx — local-storage cache first, then API
            let cached;
            try {
                const raw = getData("testimonials-" + lang, true);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    if (parsed?.length) {
                        setTestimonials(parsed);
                        cached = parsed;
                    }
                }
            } catch { /* ignore */ }
            try {
                const res = await axios.request({
                    baseURL: config.controllURL,
                    url: lang + "/api/sections/testimonials",
                    method: "get",
                });
                if (res?.data && JSON.stringify(cached) !== JSON.stringify(res.data)) {
                    saveData("testimonials-" + lang, res.data, true);
                    setTestimonials(res.data);
                }
            } catch { /* ignore */ }
        }
        fetchMainService();
        fetchOffers();
        fetchTestimonials();
    }, [lang]);

    const scrollToBooking = (serviceID = null) => {
        if (serviceID) setPreselectedServiceId(serviceID);
        const el = document.getElementById("book-card");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    };

    const filteredServices = (mainService || []).filter((item) => {
        const ar = item.DescriptionAr?.trim();
        const en = item.DescriptionEn?.trim().toLowerCase();
        return ar !== "ميكانيكا" && en !== "mechanical";
    });

    return (
        <div className="creative-maintenance v7">
            {/* ═══════ FLOATING DECORATIVE BACKGROUND (13 svgs drifting) ═══════ */}
            <FloatingMaintenanceBg />

            {/* ═══════ HERO + INLINE BOOKING ═══════ */}
            <section className="v7-hero">
                <div className="v7-hero-grid">
                    {/* Mobile mini hero */}
                    <div className="v7-mobile-mini">
                        <h2>
                            {lang === "ar" ? "احجز صيانتك " : "Book Your Service "}
                            <span style={{ color: "var(--v7-g)", fontSize: isMobile ? "1.5rem" : "3rem" }}>
                                {lang === "ar" ? "في 10 ثوانٍ" : "in 10 Seconds"}
                            </span>
                        </h2>
                        <p>{lang === "ar" ? "مركز معتمد · +60 عام خبرة · قطع غيار أصلية" : "Certified center · +60 years · Original parts"}</p>
                    </div>

                    {/* Desktop text col */}
                    <div className="v7-hero-text-col">
                        <span className="v7-tag v7-tag-gold">
                            🛡 {trans("info:maintenance_center")}
                        </span>
                        <h1>
                            {lang === "ar" ? (
                                <>احجز صيانتك<br /><span className="v7-accent sm:text-20 md:text-30" style={{ fontSize: isMobile ? "1.5rem" : "3rem" }}>في 10 ثوانٍ</span></>
                            ) : (
                                <>Book Your Service<br /><span className="v7-accent" style={{ fontSize: isMobile ? "1.5rem" : "3rem" }}>in 10 Seconds</span></>
                            )}
                        </h1>
                        <p>
                            {lang === "ar"
                                ? "مركز صيانة معتمد بخبرة تتجاوز 60 عاماً. قطع غيار أصلية وفنيون معتمدون من المصنع."
                                : "Certified maintenance center with 60+ years of experience. Original parts and factory-certified technicians."}
                        </p>

                        <div className="v7-hero-stats">
                            <div className="v7-hero-stat">
                                <div className="v7-hero-stat-val">+60</div>
                                <div className="v7-hero-stat-lbl">{lang === "ar" ? "عام خبرة" : "Years"}</div>
                            </div>
                            <div className="v7-hero-stat">
                                <div className="v7-hero-stat-val">+400K</div>
                                <div className="v7-hero-stat-lbl">{lang === "ar" ? "سيارة خُدمت" : "Cars served"}</div>
                            </div>
                            <div className="v7-hero-stat">
                                <div className="v7-hero-stat-val">+95%</div>
                                <div className="v7-hero-stat-lbl">{lang === "ar" ? "رضا الضيوف" : "Satisfaction"}</div>
                            </div>
                            <div className="v7-hero-stat">
                                <div className="v7-hero-stat-val">2</div>
                                <div className="v7-hero-stat-lbl">{lang === "ar" ? "فرع" : "Branches"}</div>
                            </div>
                        </div>

                        <div className="v7-cert-strip">
                            <div className="v7-cert-item">
                                <div className="v7-cert-icon" style={{ background: "linear-gradient(135deg,#eafbec,#d4f5db)" }}>🏆</div>
                                {lang === "ar" ? "صيانة معتمدة رسمياً" : "Officially certified"}
                            </div>
                            <div className="v7-cert-item">
                                <div className="v7-cert-icon" style={{ background: "linear-gradient(135deg,#fff7ed,#fef3c7)" }}>🛡</div>
                                {lang === "ar" ? "ضمان 12 شهر" : "12-month warranty"}
                            </div>
                            <div className="v7-cert-item">
                                <div className="v7-cert-icon" style={{ background: "linear-gradient(135deg,#eff6ff,#dbeafe)" }}>✓</div>
                                {lang === "ar" ? "معتمد من SASO" : "SASO Certified"}
                            </div>
                        </div>
                    </div>

                    {/* Booking card (inline, replaces popup) */}
                    <InlineBookingCard
                        mainServices={mainService}
                        preselectedServiceId={preselectedServiceId}
                        onConsumePreselect={() => setPreselectedServiceId(null)}
                    />
                </div>
            </section>

            {/* ═══════ PROTECTION & SHADING (live data) ═══════ */}
            <div className="v7-prot-wrap">
                <ProtectionAndShadingHomeSection />
            </div>


            {/* ═══════ SERVICES SECTION (v7) ═══════ */}
            <section className="v7-section">
                <div className="v7-section-inner">
                    <div className="v7-section-head">
                        <span className="v7-tag">{trans("info:main_services")}</span>
                        <h2>{lang === "ar" ? "خدماتنا الرئيسية" : "Our Main Services"}</h2>
                        <p>{lang === "ar" ? "اختر الخدمة التي تناسب احتياجاتك" : "Choose the service that fits your needs"}</p>
                    </div>
                    <div className="v7-services-grid">
                        {filteredServices.map((item, index) => {
                            const titleAr = item.DescriptionAr?.replace(/وميكانيكا|ميكانيكا/g, "").trim();
                            const titleEn = item.DescriptionEn?.replace(/and mechanics|mechanics/gi, "").trim();
                            return (
                                <div key={index} className="v7-service-card" onClick={() => scrollToBooking(item.MainServiceID)} role="button" tabIndex={0}>
                                    <div className="v7-service-card-icon">
                                        <span
                                            className="v7-service-card-icon-svg"
                                            style={{
                                                WebkitMaskImage: getServiceIcon(item.DescriptionEn),
                                                maskImage: getServiceIcon(item.DescriptionEn),
                                            }}
                                        />
                                    </div>
                                    <h3 className="v7-service-card-title">{lang === "ar" ? titleAr : titleEn}</h3>
                                    <p className="v7-service-card-desc">
                                        {item.Details || (lang === "ar" ? "نضمن لك أفضل جودة وأسرع وقت للتنفيذ" : "We guarantee the best quality and fastest execution time")}
                                    </p>
                                    <span className="v7-service-card-cta">
                                        {lang === "ar" ? "احجز الآن" : "Book now"} {lang === "ar" ? "‹" : "›"}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ═══════ STATS SECTION (v7) ═══════ */}
            <section className="v7-section v7-section-alt">
                <div className="v7-section-inner">
                    <div className="v7-section-head">
                        <span className="v7-tag">{lang === "ar" ? "أرقامنا" : "Our Numbers"}</span>
                        <h2>{lang === "ar" ? "إنجازاتنا بالأرقام" : "Our Achievements"}</h2>
                        <p>{lang === "ar" ? "ثقة ومصداقية تتحدث عن نفسها" : "Trust and credibility that speaks for itself"}</p>
                    </div>
                    <div className="v7-stats-grid">
                        <div className="v7-stat-card">
                            <div className="v7-stat-icon">✓</div>
                            <div className="v7-stat-num">+60</div>
                            <div className="v7-stat-lbl">{lang === "ar" ? "عاماً من الخبرة" : "Years of Experience"}</div>
                        </div>
                        <div className="v7-stat-card">
                            <div className="v7-stat-icon">⚙</div>
                            <div className="v7-stat-num">+400K</div>
                            <div className="v7-stat-lbl">{lang === "ar" ? "سيارة تمت صيانتها" : "Cars Serviced"}</div>
                        </div>
                        <div className="v7-stat-card">
                            <div className="v7-stat-icon">★</div>
                            <div className="v7-stat-num">+95%</div>
                            <div className="v7-stat-lbl">{lang === "ar" ? "رضا الضيوف" : "Guest Satisfaction"}</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ═══════ JOURNEY SECTION (v7) ═══════ */}
            <section className="v7-section">
                <div className="v7-section-inner">
                    <div className="v7-section-head">
                        <span className="v7-tag">{lang === "ar" ? "كيف نعمل" : "How it works"}</span>
                        <h2>{lang === "ar" ? "رحلة الصيانة معنا" : "Your Maintenance Journey"}</h2>
                    </div>
                    <div className="v7-journey-list">
                        {[
                            { ar: "حجز موعد", en: "Book Appointment", arDesc: "اختر الخدمة والوقت المناسب لك", enDesc: "Choose the service and time that suits you" },
                            { ar: "فحص السيارة", en: "Car Inspection", arDesc: "يقوم خبراؤنا بفحص دقيق وشامل", enDesc: "Our experts perform a precise and comprehensive check" },
                            { ar: "تنفيذ الصيانة", en: "Execution", arDesc: "تتم الصيانة بأعلى معايير الجودة", enDesc: "Maintenance completed with the highest quality standards" },
                            { ar: "استلام السيارة", en: "Delivery", arDesc: "استلم سيارتك جاهزة وبأفضل حال", enDesc: "Receive your car ready and in best condition" },
                        ].map((s, i) => (
                            <div key={i} className="v7-journey-step">
                                <div className="v7-journey-num">{i + 1}</div>
                                <h4>{lang === "ar" ? s.ar : s.en}</h4>
                                <p>{lang === "ar" ? s.arDesc : s.enDesc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ═══════ COMPARISON (v7 cmp-table) ═══════ */}
            <section className="v7-section v7-section-alt">
                <div className="v7-section-inner">
                    <div className="v7-section-head">
                        <span className="v7-tag">{lang === "ar" ? "مقارنة الخدمة" : "Service comparison"}</span>
                        <h2>{lang === "ar" ? "لماذا نحن الأفضل؟" : "Why Choose Us?"}</h2>
                    </div>
                    <div className="v7-why-grid">
                        {/* Comparison Table */}
                        <div className="v7-why-col">
                            <h3 className="v7-why-col-title">{lang === "ar" ? "مقارنة الخدمة" : "Service comparison"}</h3>
                            <div className="v7-cmp-section">
                                <div className="v7-cmp-headers">
                                    <div>{lang === "ar" ? "المزايا" : "Features"}</div>
                                    <div>{lang === "ar" ? "نحن" : "Us"}</div>
                                    <div>{lang === "ar" ? "غيرنا" : "Others"}</div>
                                </div>
                                <div className="v7-cmp-table">
                                    {[
                                        { ar: "قطع غيار أصلية 100%", en: "100% original parts", us: true, them: false },
                                        { ar: "ضمان 12 شهر على القطع", en: "12-month parts warranty", us: true, them: false },
                                        { ar: "أجهزة فحص معتمدة من المصنع", en: "Factory-certified diagnostics", us: true, them: false },
                                        { ar: "فريق عمل متخصص", en: "Specialized team", us: true, them: true },
                                        { ar: "تقرير فني رقمي بالصور", en: "Digital technical report", us: true, them: false },
                                        { ar: "فنيون معتمدون من المصنع", en: "Factory-certified technicians", us: true, them: false },
                                    ].map((row, i) => (
                                        <div key={i} className="v7-cmp-row">
                                            <span>{lang === "ar" ? row.ar : row.en}</span>
                                            <div className={row.us ? "v7-check" : "v7-cross"}>{row.us ? "✓" : "✕"}</div>
                                            <div className={row.them ? "v7-check" : "v7-cross"}>{row.them ? "✓" : "✕"}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Guest reviews (live API data — matches src/components/testimonial.jsx data shape) */}
                        <div className="v7-why-col">
                            <h3 className="v7-why-col-title">{lang === "ar" ? "آراء الضيوف" : "Guests Feedback"}</h3>

                            <div className="v7-reviews-stack">
                                {(testimonials || []).slice(0, 3).map((t, i) => {
                                    const initial = (t?.name || "G").trim().charAt(0);
                                    const palette = ["#23cc4f", "#f59e0b", "#3b82f6", "#7c3aed"];
                                    return (
                                        <div key={i} className="v7-review">
                                            <div className="v7-review-head">
                                                <div className="v7-review-avatar" style={t?.image ? undefined : { background: palette[i % palette.length] }}>
                                                    {t?.image ? (
                                                        <img src={t.image} alt={t?.name} loading="lazy" />
                                                    ) : initial}
                                                </div>
                                                <div>
                                                    <span style={{ fontSize: "1.3rem", fontWeight: 700, display: "block" }}>{t?.name}</span>
                                                </div>
                                                <div style={{ marginInlineStart: "auto", color: "#f59e0b", fontSize: "1rem" }}>★★★★★</div>
                                            </div>
                                            <p>{t?.content}</p>
                                            <div className="v7-review-source">
                                                <span className="v7-review-verified">
                                                    ✓ {lang === "ar" ? "ضيف موثّق" : "Verified Guest"}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}

                                {(!testimonials || testimonials.length === 0) && (
                                    <div className="v7-review">
                                        <p style={{ textAlign: "center", color: "#999" }}>
                                            {lang === "ar" ? "لا توجد آراء بعد" : "No reviews yet"}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>


            {/* ═══════ EXISTING SHARED SECTIONS ═══════ */}
            <div className="py-20">
                <AboutHJSection isSlider={isMobile} />
                <WhyHJSection />

                {/* MAINTENANCE OFFERS */}
                {maintenanceOffers?.length > 0 && (
                    <div className={isMobile ? "" : "ctn mb-36"}>
                        <div className="section-header !mb-10">
                            <h2 className="section-title !text-[#23cc4f]">{trans("navbar:man_offer")}</h2>
                        </div>
                        {!isMobile ? (
                            <div className="grid grid-cols-4 gap-4 mb-36">
                                {maintenanceOffers.map((offer, i) => (
                                    <Offer
                                        key={i}
                                        name={offer?.title}
                                        img={offer?.image || "/assets/images/parts/default.png"}
                                        type={offer?.type}
                                        excerpt={offer?.excerpt}
                                        endDate={offer?.endDate}
                                        startDate={offer?.startDate}
                                        totalDays={offer?.totalDays}
                                        slug={slugCombine(offer?.slug, offer?.otherLink)}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="offers-swiper-container-v7">
                                <Swiper
                                    spaceBetween={20}
                                    slidesPerView="auto"
                                    className="offers-swiper-v7"
                                    observer
                                    observeParents
                                    watchSlidesProgress
                                    loop={false}
                                >
                                    {maintenanceOffers.map((offer, i) => (
                                        <SwiperSlide key={i} style={{ width: "320px" }}>
                                            <Offer
                                                name={offer?.title}
                                                img={offer?.image || "/assets/images/parts/default.png"}
                                                type={offer?.type}
                                                excerpt={offer?.excerpt}
                                                endDate={offer?.endDate}
                                                startDate={offer?.startDate}
                                                totalDays={offer?.totalDays}
                                                slug={slugCombine(offer?.slug, offer?.otherLink)}
                                            />
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </div>
                        )}
                    </div>
                )}

                {/* APP PROMO (v7) */}
                <section className="v7-section">
                    <div className="v7-app-promo">
                        <div className="v7-app-promo-text">
                            <h2>{lang === "ar" ? "حمل تطبيقنا الآن" : "Download Our App Now"}</h2>
                            <p>
                                {lang === "ar"
                                    ? "احصل على تجربة صيانة أسهل وأسرع من خلال هاتفك. حجز المواعيد، وعروض حصرية فقط لمستخدمي التطبيق."
                                    : "Get a faster and easier maintenance experience through your phone. Book appointments, and exclusive offers only for app users."}
                            </p>
                            <div className="v7-app-stores">
                                <a href="https://apps.apple.com/sa/app/hassan-jameel-motors/id6751719846" target="_blank" rel="noreferrer">
                                    <img src="/assets/images/footer/AS.png" alt="App Store" />
                                </a>
                                <a href="https://play.google.com/store/apps/details?id=com.hassanjameel.app" target="_blank" rel="noreferrer">
                                    <img src="/assets/images/footer/GP.png" alt="Google Play" />
                                </a>
                            </div>
                        </div>
                        <div className="v7-app-promo-qr">
                            <img src="/assets/qrcode.jfif" alt={lang === "ar" ? "كود QR للتحميل" : "Download QR"} loading="lazy" />
                            <span>{lang === "ar" ? "امسح للتحميل" : "Scan to download"}</span>
                        </div>
                    </div>
                </section>
            </div>

            {/* ═══════ FINAL CTA (with WhatsApp) ═══════ */}
            <section className="v7-cta">
                <div className="v7-cta-box">
                    <div className="v7-cta-guarantee">
                        🛡 {lang === "ar" ? "ضمان استرجاع 100% في حالة عدم الرضا" : "100% money-back guarantee"}
                    </div>
                    <h2>{lang === "ar" ? "جاهز تحجز؟" : "Ready to book?"}</h2>
                    <p>{lang === "ar" ? "احجز الآن" : "Book now"}</p>
                    <div className="v7-cta-actions">
                        <button
                            className="v7-pill v7-pill-md v7-cta-book"
                            onClick={() => scrollToBooking()}
                            type="button"
                        >
                            📅 {trans("info:reserve")} {trans("info:servce")}
                        </button>
                        <ReactWhatsapp
                            className="v7-pill v7-pill-md v7-cta-wa"
                            number="+966920018996"
                            message={lang === "ar"
                                ? "السلام عليكم، أرغب في حجز خدمة صيانة لدى حسن جميل."
                                : "Hello, I would like to book a maintenance service at Hassan Jameel."}
                        >
                            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                                <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
                            </svg>
                            {trans("info:whatsapp_number")}
                        </ReactWhatsapp>
                    </div>
                </div>
            </section>

            {/* Sticky mobile CTA */}
            <div className="v7-sticky-cta">
                <button className="v7-sticky-cta-btn" onClick={() => scrollToBooking()} type="button">
                    📅 {lang === "ar" ? "احجز صيانتك الآن" : "Book service now"}
                </button>
            </div>
        </div>
    );
};

export default MainServicePage;
